import { PlayerManager } from 'chromecast-caf-receiver/cast.framework';
import {
  MessageType,
  LoadRequestData,
  ErrorData,
  ErrorType,
  StreamType,
  Image as CastImage,
} from 'chromecast-caf-receiver/cast.framework.messages';
import type { CastSession } from '../types/session';
import type { CastMedia } from '../types/media';
import { StateController, AppState } from '../state-controller';
import { Hermes } from '../network';
import { Logger } from '../logger';
import { resetQueuePrefetch } from './queue-manager';
import { initMux, signalVideoChange } from '../monitoring/mux';

/**
 * LOAD message interceptor — the entry point for all media playback.
 *
 * The sender sends the platform URL in `contentId` (e.g.
 * `https://plataforma.brasilparalelo.com.br/playlists/<slug>/media/<id>`).
 * The receiver parses the URL, fetches media metadata from Hermes via
 * `GetMediaBySlug`, and populates the request for playback.
 *
 * User authentication, language preferences and analytics are handled
 * by the custom message channel (see `user-channel.ts`), which runs
 * before the first LOAD.
 */

const LOG_TAG = 'LOAD';

/** Cloudinary transform URL prefix used by BP for thumbnails. */
const IMAGE_BASE_URL =
  'https://images.brasilparalelo.com.br/hvzbb2hdx/image/upload/c_scale,f_auto,w_1280/v1/';

// ---------------------------------------------------------------------------
// Mux reporting
// ---------------------------------------------------------------------------

/** Whether this is the first media load in the session. */
let firstPlay = true;

/** Resets the first-play flag (e.g. on new Cast session). */
export function resetFirstPlay(): void {
  firstPlay = true;
}

/** Returns whether the next LOAD will be the first play. */
export function isFirstPlay(): boolean {
  return firstPlay;
}

/**
 * Reports a newly loaded media to Mux — initializes on the first load of the
 * Cast session, signals a video change thereafter.
 *
 * Every load path (VOD and live) must route through here. A path that skips it
 * leaves the session unmonitored when it was the first load, or reporting under
 * the previous media's identity when it was not.
 */
function reportMediaToMux(
  playerManager: PlayerManager,
  videoId: string,
  videoTitle: string,
  viewerUserId: string,
  streamUrl: string,
): void {
  if (firstPlay) {
    initMux(playerManager, videoId, videoTitle, viewerUserId, streamUrl);
    firstPlay = false;
  } else {
    signalVideoChange(videoId, videoTitle, streamUrl);
  }
}

// ---------------------------------------------------------------------------
// Platform URL parser
// ---------------------------------------------------------------------------

interface ParsedPlatformUrl {
  playlistSlug: string;
  mediaId: string;
}

function parsePlatformUrl(url: string): ParsedPlatformUrl | null {
  try {
    const path = url.startsWith('http') ? new URL(url).pathname : url;
    const match = path.match(/\/playlists\/([^/]+)\/media\/([^/]+)/);
    if (!match?.[1] || !match[2]) return null;
    return { playlistSlug: match[1], mediaId: match[2] };
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Live URL parser
// ---------------------------------------------------------------------------

interface ParsedLiveUrl {
  liveId: string;
}

function parseLiveUrl(url: string): ParsedLiveUrl | null {
  try {
    const path = url.startsWith('http') ? new URL(url).pathname : url;
    const match = path.match(/\/lives\/([^/]+)/);
    if (!match?.[1]) return null;
    return { liveId: match[1] };
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Custom data typing
// ---------------------------------------------------------------------------

/**
 * Shape of `customData` stored in metadata by the receiver after Hermes
 * responds. Downstream handlers (queue, checkpoint, drm) read these fields.
 */
interface ReceiverCustomData {
  mediaId: string;
  playlistId: string;
  playlistSlug: string;
  playlistType: string;
}

/**
 * Metadata shape that includes the receiver-populated `customData` field
 * alongside the standard `GenericMediaMetadata` properties.
 *
 * `customData` is `Partial` because it arrives over the wire: queue items built
 * by `queue-manager` carry `mediaId`/`playlistId`/`playlistSlug` but no
 * `playlistType`, and a sender may send none of it.
 */
interface BPMetadata {
  title?: string;
  subtitle?: string;
  images?: CastImage[];
  customData?: Partial<ReceiverCustomData>;
  [key: string]: unknown;
}

/**
 * Recovers the media identity from `customData` when `contentId` is not a
 * platform URL. Defence in depth behind the `contentUrl` fix below.
 *
 * The bug (ID-2496): this interceptor used to write the stream URL into
 * `request.media.contentId`, and CAF hands it the queue item's own
 * `MediaInformation` object. Once an item had passed through, its `contentId`
 * was a `stream.brasilparalelo.com.br` URL rather than a platform path, so the
 * advance LOAD on media completion failed `parsePlatformUrl` and returned
 * LOAD_FAILED — Mux error 905, and a black standby screen.
 *
 * The primary fix is to write the stream URL to `contentUrl` instead, which CAF
 * documents for exactly this ("to allow using contentId for real id"), leaving
 * `contentId` untouched. This fallback still earns its place: a receiver running
 * across a deploy can hold queue items whose `contentId` was rewritten by the
 * old build, and `queue-manager` already stores the identity in `customData`.
 */
function parseCustomData(metadata: BPMetadata): ParsedPlatformUrl | null {
  const mediaId = metadata.customData?.mediaId;
  const playlistSlug = metadata.customData?.playlistSlug;
  if (!mediaId || !playlistSlug) return null;
  return { mediaId, playlistSlug };
}

// ---------------------------------------------------------------------------
// Live load handler
// ---------------------------------------------------------------------------

async function handleLiveLoad(
  playerManager: PlayerManager,
  liveId: string,
  request: LoadRequestData,
  metadata: BPMetadata,
  media: CastMedia,
  session: CastSession,
): Promise<LoadRequestData | ErrorData> {
  const error = new ErrorData(ErrorType.LOAD_FAILED);

  try {
    const result = await Hermes.client.GetLive({ id: liveId });
    const live = result.live;

    const streamUrl = live.mux_playback_url;
    if (!streamUrl) {
      Logger.error(LOG_TAG, 'Live has no mux_playback_url', { liveId });
      return error;
    }

    // Active → LIVE stream; otherwise → VOD replay (BUFFERED)
    const isActive = live.status === 'active';
    request.media.streamType = isActive ? StreamType.LIVE : StreamType.BUFFERED;
    // `contentUrl`, not `contentId` — see the note above parseCustomData().
    request.media.contentUrl = streamUrl;

    // Metadata
    metadata.title = live.name;
    metadata.subtitle = live.time_label;

    const thumbnailPath = live.thumb?.cloudinary_path;
    if (thumbnailPath) {
      const img = new CastImage(`${IMAGE_BASE_URL}${thumbnailPath}`);
      img.width = 260;
      img.height = 146;
      metadata.images = [img];
    }

    metadata.customData = {
      mediaId: liveId,
      playlistId: '',
      playlistSlug: '',
      playlistType: 'live',
    };

    request.media.metadata = metadata;

    // Update CastMedia state
    media.contentId = streamUrl;
    media.mediaId = liveId;
    media.title = live.name;
    media.subtitle = live.time_label;
    media.playlistType = 'live';

    // Store request for downstream handlers
    session.loadRequestData = request;

    reportMediaToMux(playerManager, liveId, live.name, session.userId, streamUrl);

    Logger.info(LOG_TAG, `Live loaded (${isActive ? 'LIVE' : 'REPLAY'})`, {
      liveId,
      status: live.status,
      title: live.name,
      streamUrl,
    });

    return request;
  } catch (err) {
    Logger.error(LOG_TAG, 'Failed to fetch live from Hermes', err);
    return error;
  }
}

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

/**
 * Registers the LOAD message interceptor on the given player manager.
 *
 * @param playerManager - Cast player manager instance
 * @param session       - Mutable session state (already populated by user-channel)
 * @param media         - Mutable media state (populated during LOAD)
 * @param castState     - App state controller (for UI transitions)
 */
export function registerLoadInterceptor(
  playerManager: PlayerManager,
  session: CastSession,
  media: CastMedia,
  castState: StateController,
): void {
  playerManager.setMessageInterceptor(
    MessageType.LOAD,
    async (request: LoadRequestData): Promise<LoadRequestData | ErrorData> => {
      const error = new ErrorData(ErrorType.LOAD_FAILED);
      const metadata = (request.media.metadata ?? {}) as BPMetadata;

      // Reset queue prefetch guard for the new media
      resetQueuePrefetch();

      // ---------------------------------------------------------------
      // 1. LIVE URL — /lives/<id> from platform
      // ---------------------------------------------------------------
      const liveUrlParsed = parseLiveUrl(request.media.contentId ?? '');
      if (liveUrlParsed) {
        castState.set(AppState.LOADING);
        return handleLiveLoad(
          playerManager,
          liveUrlParsed.liveId,
          request,
          metadata,
          media,
          session,
        );
      }

      // ---------------------------------------------------------------
      // 2. Transition to LOADING — hides standby immediately
      // ---------------------------------------------------------------
      castState.set(AppState.LOADING);

      // ---------------------------------------------------------------
      // 3. Resolve the media identity — platform URL, else customData
      // ---------------------------------------------------------------
      const contentId = request.media.contentId ?? '';
      const fromUrl = parsePlatformUrl(contentId);
      const parsed = fromUrl ?? parseCustomData(metadata);

      if (!parsed) {
        Logger.error(LOG_TAG, 'Cannot resolve media from contentId or customData', {
          contentId,
          customData: metadata.customData,
        });
        return error;
      }

      if (!fromUrl) {
        // Recovered a queue item whose contentId we had already rewritten to
        // the stream URL. See parseCustomData().
        Logger.info(LOG_TAG, 'Resolved media from customData (contentId was rewritten)', {
          contentId,
          mediaId: parsed.mediaId,
        });
      }

      const { mediaId, playlistSlug } = parsed;

      // ---------------------------------------------------------------
      // 4. Fetch media from Hermes via GetMediaBySlug
      // ---------------------------------------------------------------
      try {
        const result = await Hermes.client.GetMediaBySlug({
          mediaId,
          playlistSlug,
        });
        const hermesMedia = result.media;

        // -- Stream URL (experiment-aware: see `manifest` vs deprecated `resource`) --
        const streamUrl = hermesMedia.manifest.url;
        // `contentUrl`, not `contentId` — see the note above parseCustomData().
        request.media.contentUrl = streamUrl;

        // -- Duration & checkpoint --
        request.media.duration = hermesMedia.duration;

        // Use sender-provided currentTime if set, otherwise use Hermes checkpoint
        if (request.currentTime == null) {
          request.currentTime = hermesMedia.checkpoint ?? 0;
        }

        // -- Metadata (title, subtitle, thumbnail, customData) --
        metadata.title = hermesMedia.name;
        metadata.subtitle = hermesMedia.playlist.name;

        const dvdPath = hermesMedia.playlist.thumbnails?.dvd?.image;
        const thumbPath = hermesMedia.thumbnail?.image;
        const thumbnailPath = dvdPath ?? thumbPath;

        if (thumbnailPath) {
          const img = new CastImage(`${IMAGE_BASE_URL}${thumbnailPath}`);
          if (dvdPath) {
            img.width = 96;
            img.height = 146;
          } else {
            img.width = 260;
            img.height = 146;
          }
          metadata.images = [img];
        }

        // Populate customData for downstream handlers (queue, checkpoint, drm)
        metadata.customData = {
          mediaId,
          playlistId: hermesMedia.playlist.id,
          playlistSlug,
          playlistType: hermesMedia.playlist.type_str,
        };

        request.media.metadata = metadata;

        // -- Update CastMedia state --
        media.contentId = streamUrl;
        media.mediaId = mediaId;
        media.title = hermesMedia.name;
        media.subtitle = hermesMedia.playlist.name;
        media.playlistType = hermesMedia.playlist.type_str;

        // -- Store full request for downstream handlers (queue, tracks, checkpoint) --
        session.loadRequestData = request;

        // ---------------------------------------------------------------
        // 5. Mux reporting
        // ---------------------------------------------------------------
        reportMediaToMux(playerManager, mediaId, hermesMedia.name, session.userId, streamUrl);

        Logger.info(LOG_TAG, 'Media loaded from Hermes', {
          mediaId,
          playlistSlug,
          playlistId: hermesMedia.playlist.id,
          title: hermesMedia.name,
          duration: hermesMedia.duration,
          checkpoint: request.currentTime,
          playlistType: hermesMedia.playlist.type_str,
        });

        return request;
      } catch (err) {
        Logger.error(LOG_TAG, 'Failed to fetch media from Hermes', err);
        return error;
      }
    },
  );
}
